// Deep-link entry helpers for the "your analysis is ready" email
// (Phase 3.1). Pure + dependency-free so the parsing + state mapping are
// unit-testable without mounting App.
//
// The run-token rides in the URL FRAGMENT (#run=…&token=…), NOT the query
// string — a fragment is never sent to the server, so the token stays out of
// access logs and the Referer header. (The email is still not E2E; the token
// grants read of that one run's result for the retention window — the same
// magic-link tradeoff the recovery link makes.)

export type DeepLink = { runId: string; runToken: string };

/** Parse `#run=<id>&token=<tok>` from a location hash (with or without the
 *  leading '#'). Returns null unless BOTH are present. */
export function parseDeepLink(hash: string): DeepLink | null {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!raw) return null;
  const sp = new URLSearchParams(raw);
  const runId = sp.get("run");
  const runToken = sp.get("token");
  return runId && runToken ? { runId, runToken } : null;
}

/** Phone/tablet check for the deep-link landing's "this file needs a
 *  computer" hint. UA-string based so it stays pure/testable; the caller
 *  passes `navigator.maxTouchPoints` to catch iPadOS, which masquerades
 *  as desktop Macintosh in its UA. */
export function isMobileUa(ua: string, maxTouchPoints = 0): boolean {
  if (/Android|iPhone|iPad|iPod/i.test(ua)) return true;
  return /Macintosh/.test(ua) && maxTouchPoints > 1;
}

export type DeepLinkUiState =
  | "loading"
  | "ready"
  | "running"
  | "expired"
  // The status check itself failed (network drop, server 5xx) — the link may
  // be perfectly good. Distinct from "expired" so a wifi blip on the ready
  // email never tells a user their analysis is gone (sweep, 2026-07-10).
  | "unreachable";

/** Map a run's chain_state to the landing UI state. Anything terminal-but-not-
 *  complete, or unknown, is "expired" — the link no longer leads anywhere
 *  useful. (A purged 404/410 vs a transport failure is branched by the
 *  resolver's catch in App.tsx, not here.) */
export function classifyChainState(chainState: string): DeepLinkUiState {
  if (chainState === "run_complete") return "ready";
  if (
    chainState === "running" ||
    chainState === "initializing" ||
    chainState === "queued"
  )
    return "running";
  return "expired";
}
